import dayjs from 'dayjs';

import { canOrderForChild } from '@/helper/dashboardStatus';
import { unpaidTotalForLines } from '@/helper/cartFamily';
import { getCutOffTime } from '@/helper/getCutOffTime';
import getRemainingTime from '@/helper/getRemainingTime';

/** Group cart lines: { [customerId]: { [dateIso]: lines[] } } */
export function groupCartByChildThenDate(items) {
	const byChild = {};
	(items || []).forEach((product) => {
		const childId = product.customerId != null ? String(product.customerId) : 'unknown';
		const dateKey = dayjs(product.date).format('YYYY-MM-DD');
		byChild[childId] = byChild[childId] ?? {};
		byChild[childId][dateKey] = byChild[childId][dateKey] ?? [];
		byChild[childId][dateKey].push(product);
	});
	return byChild;
}

export function buildProcessBulkPayload({
	linesByDate,
	customerId,
	outletId,
	payTypeDeposit,
	orderType,
	utcOffset,
	getSTime,
}) {
	return Object.keys(linesByDate).map((d) => {
		const pItems = linesByDate[d];
		return {
			outlet: outletId,
			customer: customerId,
			note: pItems[0]?.notes || '',
			date: d,
			products: pItems.map((p) => {
				const objP = {
					product: p.id,
					quantity: p.quantity,
					is_void: p.is_void,
				};
				if (p.add_ons?.length) {
					objP.add_ons = p.add_ons.map((m) => m.add_on.id);
				}
				if (p.modifiers?.length) {
					objP.modifiers = p.modifiers.map((m) => m.modifier.id);
				}
				objP.serving_time = p.serving_time?.id || null;
				if (p.discount?.type) {
					objP.discount = +p.discount?.value || 0;
					objP.discount_type = p.discount?.type || null;
				}
				if (p.serving_time) {
					const { isDisable } = getSTime
						? getSTime(p.serving_time, dayjs(p.date))
						: defaultGetSTime(p.serving_time, dayjs(p.date), utcOffset);
					objP.isValid = !isDisable;
				} else {
					objP.isValid = false;
				}
				objP.note = p.notes || null;
				return objP;
			}),
			payment_type: payTypeDeposit,
			type: orderType,
		};
	});
}

function defaultGetSTime(s, orderDate, utcOffset) {
	const { oCutOff } = getCutOffTime(s, orderDate, utcOffset);
	const remainingDayTime = getRemainingTime(oCutOff);
	return { isDisable: remainingDayTime < 0 };
}

/**
 * Split family cart into payable vs short-balance children.
 */
export function classifyFamilyPay({ items, children, outlet }) {
	const grouped = groupCartByChildThenDate(items);
	const payable = [];
	const short = [];

	Object.keys(grouped).forEach((childKey) => {
		if (childKey === 'unknown') return;
		const childId = Number(childKey);
		const child = children.find((c) => Number(c.id) === childId);
		const lines = Object.values(grouped[childKey]).flat();
		const total = unpaidTotalForLines(lines);
		const balance = +child?.balance || 0;
		const after = balance - total;
		const minLimit =
			outlet?.use_minimum_balance_limit && outlet.minimum_balance_limit != null
				? +outlet.minimum_balance_limit
				: null;
		const hitsMin = minLimit != null && after <= minLimit;
		const inactive = !canOrderForChild(child);
		const entry = {
			childId,
			child,
			linesByDate: grouped[childKey],
			lines,
			total,
			balance,
			after,
			shortfall: after < 0 ? Math.abs(after) : 0,
		};
		if (inactive || after < 0 || hitsMin) {
			short.push(entry);
		} else {
			payable.push(entry);
		}
	});

	return { payable, short, grouped };
}
